import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type {
  EstadoDaConsultaTransito,
  ListarPendenciasSecretariaResult,
} from '@/features/secretaria-arbitral/types';
import { agruparPendencias } from '@/features/secretaria-arbitral/utils/pendenciasAgrupadas';
import { FILTRO_PENDENCIA_PADRAO } from '@/features/secretaria-arbitral/utils/pendenciasLabels';
import { ehStatusArbitralTerminal } from '@/shared/utils/statusArbitralTerminal';

const selectState = (state: RootState) => state.secretariaArbitral;

export const selectPainelGeralCounts = createSelector(selectState, (s) => s.painelGeral);

export const selectPorStatus = (key: string) =>
  createSelector(
    selectState,
    (s) => s.porStatus[key] ?? { items: [], loading: false, error: null },
  );

/** A listagem da Tela 1. Desde o web#1090 é exclusiva dela — a fila do painel tem slot próprio. */
export const selectProcessosLista = createSelector(selectState, (s) => s.processos);

/** A fila do PAINEL de triagem. Separada de `processos` para o painel nunca ler o que a Tela 1 deixou. */
export const selectFilaTriagem = createSelector(selectState, (s) => s.filaTriagem);

export const selectListaTriagem = createSelector(selectState, (s) => s.listaTriagem);

export const selectResumoTriagem = createSelector(selectState, (s) => s.resumoTriagem);

export const selectCasoTriagemAlvo = createSelector(selectState, (s) => s.casoTriagemAlvo);

export const selectBuscaFilaTriagem = createSelector(selectState, (s) => s.buscaFilaTriagem);

export const selectElegiveisTriagem = createSelector(selectState, (s) => s.elegiveisTriagem);

export const selectLoteProcessos = createSelector(selectState, (s) => s.lote);

export const selectPrazos = createSelector(selectState, (s) => s.prazos);

export const selectSorteioSugestao = (processoId: string) =>
  createSelector(selectState, (s) => ({
    sugestao: s.sorteioSugestao.byProcessoId[processoId] ?? null,
    loading: s.sorteioSugestao.loading,
    error: s.sorteioSugestao.error,
  }));

export const selectActionLoading = createSelector(selectState, (s) => s.actionLoading);

export const selectLastError = createSelector(selectState, (s) => s.lastError);

export const selectDetalheProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    detalhe: s.detalheProcesso.byId[processoId] ?? null,
    loading: s.detalheProcesso.loadingById[processoId] ?? false,
    error: s.detalheProcesso.errorById[processoId] ?? null,
  }));

export const selectManifestacoesProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    items: s.manifestacoesProcesso.byProcessoId[processoId] ?? [],
    loading: s.manifestacoesProcesso.loading,
    error: s.manifestacoesProcesso.error,
    erroAcao: s.manifestacoesProcesso.erroAcao,
  }));

const RESULTADO_PENDENCIAS_VAZIO: ListarPendenciasSecretariaResult = {
  items: [],
  total: 0,
  contagem: { ativas: 0, finalizadas: 0, todas: 0 },
};

export const selectPendenciasProcesso = (processoId: string) =>
  createSelector(selectState, (s) => {
    const resultado = s.pendenciasProcesso.byProcessoId[processoId] ?? RESULTADO_PENDENCIAS_VAZIO;
    return {
      items: resultado.items,
      total: resultado.total,
      loading: s.pendenciasProcesso.loadingPorProcesso[processoId] ?? false,
      error: s.pendenciasProcesso.errorPorProcesso[processoId] ?? null,
    };
  });

/** Filtro aceso na tela. Fonte única para o painel e para a releitura pós-mutação. */
export const selectFiltroPendencias = (processoId: string) =>
  createSelector(
    selectState,
    (s) => s.pendenciasProcesso.filtroPorProcesso[processoId] ?? FILTRO_PENDENCIA_PADRAO,
  );

/**
 * Diz se o processo do detalhe já terminou. Seletor estreito de propósito: lê só o status, e
 * não o `selectDetalheProcesso`, que monta objeto novo a cada chamada e invalidaria o memo dos
 * cards. Detalhe ainda não carregado conta como não encerrado (o core segue sendo quem recusa).
 */
export const selectProcessoEncerrado = (processoId: string) => (state: RootState) =>
  ehStatusArbitralTerminal(
    state.secretariaArbitral.detalheProcesso.byId[processoId]?.statusArbitral,
  );

/** Um card por ato de criação — ver `agruparPendencias`. */
export const selectPendenciasAgrupadas = (processoId: string) =>
  createSelector(
    [selectPendenciasProcesso(processoId), selectProcessoEncerrado(processoId)],
    ({ items }, processoEncerrado) => agruparPendencias(items, { processoEncerrado }),
  );

/**
 * Contagem vinda do servidor, do processo INTEIRO. Contar a página exibida faria o filtro
 * "Ativas (3)" aparecer ao lado de duas linhas assim que a paginação entrasse.
 */
export const selectContagemPendencias = (processoId: string) =>
  createSelector(
    selectState,
    (s) =>
      s.pendenciasProcesso.byProcessoId[processoId]?.contagem ??
      RESULTADO_PENDENCIAS_VAZIO.contagem,
  );

export const selectPrazoPendenciaPreview = createSelector(
  selectState,
  (s) => s.prazoPendenciaPreview,
);

export const selectRanking = createSelector(selectState, (s) => s.ranking);

export const selectArList = createSelector(selectState, (s) => ({
  items: s.ar.items,
  total: s.ar.total,
  page: s.ar.page,
  limit: s.ar.limit,
  resumo: s.ar.resumo,
  porOrigem: s.ar.porOrigem,
  loading: s.ar.loading,
  error: s.ar.error,
  reemitindoSolicitacaoId: s.ar.reemitindoSolicitacaoId,
}));

export const selectArAEnviar = createSelector(selectState, (s) => ({
  items: s.arAEnviar.items,
  /** Do SERVIDOR — pagina a tabela, e reage à busca. */
  total: s.arAEnviar.total,
  /** Do SERVIDOR e sem a busca — é o número da contagem da aba. */
  totalGeral: s.arAEnviar.totalGeral,
  page: s.arAEnviar.page,
  limit: s.arAEnviar.limit,
  loading: s.arAEnviar.loading,
  error: s.arAEnviar.error,
  solicitando: s.arAEnviar.solicitando,
}));

export const selectRequeridoAEnviarDetalhe = (parteArbitralId: string) =>
  createSelector(selectState, (s) => ({
    detalhe: s.arAEnviar.detalheByParteArbitralId[parteArbitralId] ?? null,
    loading: s.arAEnviar.detalheLoading,
    error: s.arAEnviar.detalheError,
  }));

export const selectCotacaoEnvioAr = createSelector(selectState, (s) => s.arAEnviar.cotacao);

export const selectArDetalhe = (envioFisicoId: string) =>
  createSelector(selectState, (s) => ({
    detalhe: s.ar.detalheById[envioFisicoId] ?? null,
    loading: s.ar.detalheLoading,
    error: s.ar.detalheError,
  }));

export const selectArDoProcesso = (processoId: string) =>
  createSelector(selectState, (s) => s.ar.porProcesso[processoId] ?? []);

export const selectPartesProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    items: s.partesProcesso.byProcessoId[processoId] ?? [],
    loading: s.partesProcesso.loading,
    error: s.partesProcesso.error,
  }));

export const selectAdvogadosDaParte = (parteArbitralId: string) =>
  createSelector(selectState, (s) => ({
    items: s.advogadosParte.byParteArbitralId[parteArbitralId] ?? [],
    loading: s.advogadosParte.loading,
    error: s.advogadosParte.error,
  }));

export const selectDocumentosProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    items: s.documentosProcesso.byProcessoId[processoId] ?? [],
    loading: s.documentosProcesso.loading,
    error: s.documentosProcesso.error,
  }));

export const selectPublicacaoAndamento = createSelector(selectState, (s) => s.publicacaoAndamento);

export const selectSolicitacoesProcesso = createSelector(
  selectState,
  (s) => s.solicitacoesProcesso,
);

export const selectDecisaoSolicitacao = createSelector(selectState, (s) => s.decisaoSolicitacao);

export const selectConclusoSolicitacao = createSelector(selectState, (s) => s.conclusoSolicitacao);

export const selectModelosAndamentoOpcoes = createSelector(
  selectState,
  (s) => s.modelosAndamentoOpcoes,
);

export const selectCalendariosPorAno = createSelector(selectState, (s) => s.diasNaoUteis.byAno);

export const selectAndamentosProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    items: s.andamentosProcesso.byProcessoId[processoId] ?? [],
    loading: s.andamentosProcesso.loadingByProcessoId[processoId] ?? false,
    error: s.andamentosProcesso.errorByProcessoId[processoId] ?? null,
  }));

export const selectAuditoriaTriagem = (processoId: string) =>
  createSelector(selectState, (s) => {
    const estado = s.auditoriaTriagem.byProcessoId[processoId];
    return {
      page: estado?.page ?? null,
      loading: estado?.loading ?? false,
      error: estado?.error ?? null,
    };
  });

export const selectExportacaoAuditoriaTriagem = createSelector(selectState, (s) => ({
  loading: s.exportacaoAuditoria.loading,
  error: s.exportacaoAuditoria.error,
}));

export const selectCertificadosProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    items: s.certificadosProcesso.byProcessoId[processoId] ?? [],
    loading: s.certificadosProcesso.loading,
    error: s.certificadosProcesso.error,
  }));

export const selectCertificadoAcessosProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    certificado: s.certificadoAcessosProcesso.byProcessoId[processoId] ?? null,
    loading: s.certificadoAcessosProcesso.loadingById[processoId] ?? false,
    error: s.certificadoAcessosProcesso.errorById[processoId] ?? null,
  }));

export const selectChatProcesso = (processoId: string) =>
  createSelector(selectState, (s) => ({
    page: s.chatProcesso.byProcessoId[processoId] ?? null,
    loading: s.chatProcesso.loading,
    error: s.chatProcesso.error,
  }));

export const selectAlertasAtivos = createSelector(selectState, (s) => s.alertas);

export const selectAlertasPorProcesso = (processoId: string) =>
  createSelector(selectState, (s) => s.alertas.byProcessoId[processoId] ?? []);

export const selectTriagemChecklists = createSelector(selectState, (s) => s.triagemChecklists);

export const selectChecklistDoProcesso = (processoId: string) =>
  createSelector(selectTriagemChecklists, (map) => map[processoId] ?? null);

export const selectTriagemAvaliacoes = createSelector(selectState, (s) => s.triagemAvaliacoes);

export const selectTriagemIniciadosLocais = createSelector(
  selectState,
  (s) => s.triagemIniciadosLocais,
);

export const selectTriagemDecisoesAgendadas = createSelector(
  selectState,
  (s) => s.triagemDecisoesAgendadas,
);

export const selectTriagemDespejoNosAjustes = createSelector(
  selectState,
  (s) => s.triagemDespejoNosAjustes,
);

/**
 * A elegibilidade do trânsito já traduzida para a máquina de estados que o diálogo consome.
 *
 * As três fatias do slice (`loading`, `error`, `dado`) viram UM valor discriminado aqui, e não no
 * componente: derivar lá permitia o estado impossível "erro e dado ao mesmo tempo", que na
 * prática liberava o botão sobre resposta velha (COR-02).
 *
 * Nada em vôo é "sem consulta": um processo cujo diálogo nunca abriu também devolve
 * `carregando`, porque o único consumidor só renderiza com o diálogo aberto — e "não sei ainda"
 * nunca pode virar permissão.
 */
export const selectElegibilidadeTransito = createSelector(
  [selectState, (_state: RootState, processoId: string) => processoId],
  (s, processoId): EstadoDaConsultaTransito => {
    const erro = s.elegibilidadeTransito.errorById[processoId];
    if (erro) return { fase: 'erro', mensagem: erro };
    const elegibilidade = s.elegibilidadeTransito.byProcessoId[processoId];
    if (!elegibilidade || s.elegibilidadeTransito.loadingById[processoId]) {
      return { fase: 'carregando' };
    }
    return { fase: 'ok', elegibilidade };
  },
);
